import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import { Calendar, ChevronDown, ChevronUp, ChevronsUpDown, ChevronLeft, RefreshCw, RotateCcw, Search, X, Printer, FileText, ExternalLink, Eye, EyeOff } from 'lucide-react';
import { customersApi, vouchersApi } from '../services/api';
import { useToast } from '../components/Toast/Toast';
import { useAuth } from '../context/AuthContext';
import useReportKeyboardNav from '../hooks/useReportKeyboardNav';
import useChangePeriodKey from '../hooks/useChangePeriodKey';

const fmt = (n: any) =>
  Number(n || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

// Local YYYY-MM-DD — toISOString shifts to UTC, which silently turns
// April 1 IST midnight into "2026-03-31".
const toInputDate = (d: Date) => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};
const displayDate = (s?: string | null) => {
  if (!s) return '';
  const d = new Date(s);
  return `${String(d.getDate()).padStart(2, '0')}-${String(d.getMonth() + 1).padStart(2, '0')}-${d.getFullYear()}`;
};

function fyBounds(d: Date) {
  const y = d.getMonth() >= 3 ? d.getFullYear() : d.getFullYear() - 1;
  return { from: new Date(y, 3, 1), to: new Date(y + 1, 2, 31) };
}

type Row = {
  vch_id: number;
  vch_no: string | null;
  vch_date: string | null;
  vch_type_name: string | null;
  vch_subtype_name: string | null;
  is_first: boolean;
  is_last: boolean;
  particulars: string;
  debit: number;
  credit: number;
  running_balance: number | null;
  remark: string | null;
  bill_names: string | null;
};
type SortKey = 'vch_date' | 'vch_no' | 'vch_type_name' | 'particulars' | 'debit' | 'credit';
type SortDir = 'asc' | 'desc';

const STORAGE_KEY = 'ledger-report-filters';

export default function LedgerReport() {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { showError } = useToast();
  const { canEdit } = useAuth();
  // Voucher edit allowed via any of: vouchers.edit (granular), the
  // ledger-report's own .edit (so report-only users can drill in), or
  // activities.edit (legacy umbrella).
  const canEditVouchers = canEdit('vouchers') || canEdit('reports_ledger') || canEdit('activities');

  // Defaults: this FY → today.
  const today = new Date();
  const { from: fyFrom } = fyBounds(today);

  // Only the date filters are restored from storage. The ledger itself is
  // intentionally not — refresh always starts with no party selected so the
  // user gets a clean picker (matches Tally's behaviour).
  const initial = (() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (saved) return JSON.parse(saved);
    } catch { /* ignore */ }
    return null;
  })();

  const urlLedgerId = searchParams.get('ledger_id');
  // When we drill into a voucher we stamp the full report state onto the URL
  // (date_from / date_to / search) so returning here — via the voucher's
  // navigate(-1) after save — restores the exact same ledger AND filter.
  const urlDateFrom = searchParams.get('date_from');
  const urlDateTo   = searchParams.get('date_to');
  const urlSearch   = searchParams.get('search');
  // When we return from a drilled-into voucher the URL carries explicit dates;
  // pre-mark the snap as "done" for that ledger so load() keeps the preserved
  // filter instead of snapping back to the latest entry's month.
  const snapDoneRef = useRef<number | null>(urlDateFrom && urlLedgerId ? Number(urlLedgerId) : null);

  const [showControls, setShowControls] = useState(false);
  // Alt+F2 — Tally's Change Period: open the date filter, cursor in From.
  useChangePeriodKey(() => setShowControls(true));
  const [ledgerId, setLedgerId] = useState<number | null>(urlLedgerId ? Number(urlLedgerId) : null);
  const [ledgerName, setLedgerName] = useState<string>('');
  // Dates: an explicit URL date wins (restored drill-through); else, when
  // arriving from Group Summary with just ledger_id, use full FY; else storage.
  const [dateFrom, setDateFrom] = useState<string>(urlDateFrom ?? (urlLedgerId ? toInputDate(fyFrom) : (initial?.dateFrom ?? toInputDate(fyFrom))));
  const [dateTo, setDateTo]     = useState<string>(urlDateTo ?? (urlLedgerId ? toInputDate(today) : (initial?.dateTo ?? toInputDate(today))));
  const [search, setSearch] = useState(urlSearch ?? '');
  const [debouncedSearch, setDebouncedSearch] = useState('');

  const [picker, setPicker] = useState('');
  const [pickerOpen, setPickerOpen] = useState(false);
  const [pickerResults, setPickerResults] = useState<any[]>([]);
  const [pickerLoading, setPickerLoading] = useState(false);

  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState<Row[]>([]);
  const [opening, setOpening] = useState(0);
  const [closing, setClosing] = useState(0);
  const [totals, setTotals] = useState({ debit: 0, credit: 0 });

  const [sortKey, setSortKey] = useState<SortKey>('vch_date');
  const [sortDir, setSortDir] = useState<SortDir>('asc');

  // Persist only the date range — never the ledger choice. Reload should
  // start with a clean ledger picker.
  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify({ dateFrom, dateTo }));
    } catch { /* ignore */ }
  }, [dateFrom, dateTo]);

  // Debounce search-within-results
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search), 300);
    return () => clearTimeout(t);
  }, [search]);

  // Picker autocomplete (debounced)
  useEffect(() => {
    if (!picker.trim()) { setPickerResults([]); return; }
    const t = setTimeout(async () => {
      setPickerLoading(true);
      try {
        const res = await customersApi.searchAllLedgers(picker.trim());
        setPickerResults(res.success ? res.data : []);
      } catch { setPickerResults([]); }
      finally { setPickerLoading(false); }
    }, 250);
    return () => clearTimeout(t);
  }, [picker]);

  const load = useCallback(async () => {
    if (!ledgerId) { setRows([]); setOpening(0); setClosing(0); return; }
    setLoading(true);
    try {
      const res = await vouchersApi.getLedger({
        ledger_id: ledgerId,
        date_from: dateFrom || undefined,
        date_to: dateTo || undefined,
        search: debouncedSearch || undefined,
      });
      if (res.success) {
        const newRows: Row[] = res.data.rows || [];
        setRows(newRows);
        setOpening(res.data.opening || 0);
        setClosing(res.data.closing || 0);
        setTotals({ debit: res.data.totalDebit || 0, credit: res.data.totalCredit || 0 });
        if (res.data.ledger && res.data.ledger.company !== ledgerName) {
          setLedgerName(res.data.ledger.company);
        }
        // Snap "From"/"To" to the most recent entry's month — but only on
        // the first load for this ledger so manual date changes aren't overridden.
        if (snapDoneRef.current !== ledgerId) {
          snapDoneRef.current = ledgerId;
          let latest: Date | null = null;
          for (const r of newRows) {
            if (!r.vch_date) continue;
            const d = new Date(r.vch_date);
            if (!latest || d > latest) latest = d;
          }
          if (latest) {
            const monthStart = toInputDate(new Date(latest.getFullYear(), latest.getMonth(), 1));
            const monthEnd = toInputDate(new Date(latest.getFullYear(), latest.getMonth() + 1, 0));
            if (monthStart !== dateFrom) setDateFrom(monthStart);
            if (monthEnd !== dateTo) setDateTo(monthEnd);
          }
        }
      }
    } catch {
      showError('Error', 'Failed to load ledger');
    } finally {
      setLoading(false);
    }
  }, [ledgerId, dateFrom, dateTo, debouncedSearch, ledgerName, showError]);

  useEffect(() => { load(); }, [load]);

  const selectLedger = (l: any) => {
    setLedgerId(l.id);
    setLedgerName(l.company);
    setPicker('');
    setPickerOpen(false);
    setPickerResults([]);
  };

  const clearLedger = () => {
    setLedgerId(null);
    setLedgerName('');
    setRows([]);
    setOpening(0);
    setClosing(0);
  };

  const resetDates = () => {
    setDateFrom(toInputDate(fyFrom));
    setDateTo(toInputDate(today));
  };

  // Sorting must keep multi-line vouchers grouped — sort by voucher group,
  // then preserve the contra-line order within each voucher.
  const sorted = useMemo(() => {
    if (rows.length === 0) return rows;
    // Build voucher groups (preserving contra order within).
    const groups: Row[][] = [];
    let cur: Row[] = [];
    for (const r of rows) {
      if (r.is_first && cur.length) { groups.push(cur); cur = []; }
      cur.push(r);
    }
    if (cur.length) groups.push(cur);

    const dir = sortDir === 'asc' ? 1 : -1;
    groups.sort((ga, gb) => {
      const a = ga[0]; const b = gb[0];
      if (sortKey === 'vch_date') {
        const at = a.vch_date ? new Date(a.vch_date).getTime() : 0;
        const bt = b.vch_date ? new Date(b.vch_date).getTime() : 0;
        return (at - bt) * dir;
      }
      if (sortKey === 'debit')   return ((ga.reduce((s, r) => s + r.debit, 0))  - (gb.reduce((s, r) => s + r.debit, 0)))  * dir;
      if (sortKey === 'credit')  return ((ga.reduce((s, r) => s + r.credit, 0)) - (gb.reduce((s, r) => s + r.credit, 0))) * dir;
      const av = a[sortKey] as any;
      const bv = b[sortKey] as any;
      return String(av ?? '').localeCompare(String(bv ?? '')) * dir;
    });
    return groups.flat();
  }, [rows, sortKey, sortDir]);

  // No pagination: a ledger is a continuous statement, so every voucher in the
  // chosen period renders at once and the user scrolls it as one run. The
  // period itself is the limit — narrow the dates to shorten the list. Opening /
  // Total / Closing come from the backend aggregate over the same range, so
  // they already matched the full period rather than any one page.
  //
  // `printing` no longer changes WHAT is rendered (everything always is); it
  // still gates the keyboard nav while the print dialog is up, and an
  // afterprint listener clears it if the user cancels.
  const [printing, setPrinting] = useState(false);
  // ?bill_wise=1 deep-links straight into the bill-wise outstanding modal —
  // the drill-down target of the user-wise month matrix.
  const [showBillWise, setShowBillWise] = useState(searchParams.get('bill_wise') === '1' && !!urlLedgerId);

  // Keyboard parity with the reports that drill INTO this one. The ledger is a
  // statement, not a row list, so rowCount 0: arrows/PageUp/Home scroll the
  // table and Esc/Backspace go back — previously neither key did anything here,
  // stranding anyone who had navigated in from the keyboard. The hook already
  // stands down while a field has focus, and we disable it outright behind the
  // filter panel and the bill-wise modal so it can't steal their keys.
  const scrollRef = useRef<HTMLDivElement>(null);
  useReportKeyboardNav({
    rowCount: 0,
    containerRef: scrollRef,
    enabled: !showControls && !showBillWise && !printing,
    onBack: () => navigate(-1),
  });

  // Drill into a voucher from the Bill-Wise modal. Before leaving, stamp the
  // current ledger + filters onto THIS page's URL (replace, no new history
  // entry) so that when the voucher saves and calls navigate(-1), it lands
  // back on the very same ledger with the very same filter applied.
  const openVoucherFromReport = useCallback((vchId: number) => {
    const params = new URLSearchParams();
    if (ledgerId) params.set('ledger_id', String(ledgerId));
    params.set('date_from', dateFrom);
    params.set('date_to', dateTo);
    if (debouncedSearch) params.set('search', debouncedSearch);
    const back = `${location.pathname}?${params.toString()}`;
    navigate(back, { replace: true });
    navigate(`/billing/vouchers/edit/${vchId}`, {
      state: { readOnly: !canEditVouchers, returnTo: back },
    });
  }, [ledgerId, dateFrom, dateTo, debouncedSearch, navigate, location.pathname, canEditVouchers]);
  useEffect(() => {
    const onAfter = () => setPrinting(false);
    window.addEventListener('afterprint', onAfter);
    return () => window.removeEventListener('afterprint', onAfter);
  }, []);
  const handlePrint = () => {
    if (!ledgerId) return;
    setPrinting(true);
    // Defer print() so React commits the all-rows render first; otherwise
    // the dialog opens against the still-paginated DOM.
    setTimeout(() => window.print(), 50);
  };
  const voucherGroups = useMemo(() => {
    const groups: Row[][] = [];
    let cur: Row[] = [];
    for (const r of sorted) {
      if (r.is_first && cur.length) { groups.push(cur); cur = []; }
      cur.push(r);
    }
    if (cur.length) groups.push(cur);
    return groups;
  }, [sorted]);
  // Every row, always. Kept as a memo over voucherGroups (rather than using
  // `sorted` directly) so the grouping stays the single source of row order —
  // multi-line vouchers remain contiguous.
  const visibleRows = useMemo(() => voucherGroups.flat(), [voucherGroups]);

  const toggleSort = (k: SortKey) => {
    if (sortKey === k) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortKey(k); setSortDir('asc'); }
  };

  const SortIcon = ({ col }: { col: SortKey }) => {
    if (sortKey !== col) return <ChevronsUpDown size={11} className="inline-block ml-1 text-slate-400" />;
    return sortDir === 'asc'
      ? <ChevronUp size={11} className="inline-block ml-1 text-blue-600" />
      : <ChevronDown size={11} className="inline-block ml-1 text-blue-600" />;
  };

  // Click vch no. → open the voucher in the Vouchers page via a URL
  // (`/billing/vouchers/edit/:id`) so the route reflects which voucher is
  // open and refresh preserves the edit context. The readOnly flag rides
  // along in state — editable when the user has voucher / activities edit
  // perm, otherwise read-only (every input disabled, Save hidden).
  // Build the URL that fully describes the current report state (ledger +
  // filter) so we can both stamp it onto the history entry (back-button) and
  // hand it to the voucher as an explicit returnTo (save).
  const reportReturnUrl = () => {
    const params = new URLSearchParams();
    if (ledgerId) params.set('ledger_id', String(ledgerId));
    params.set('date_from', dateFrom);
    params.set('date_to', dateTo);
    if (debouncedSearch) params.set('search', debouncedSearch);
    return `${location.pathname}?${params.toString()}`;
  };

  const openVoucher = (vchId: number) => {
    // Stamp the report URL onto THIS history entry (replace, no new entry) so
    // the browser Back button returns to the same ledger + filter, and also
    // pass it as returnTo so a save lands there deterministically.
    const back = reportReturnUrl();
    navigate(back, { replace: true });
    navigate(`/billing/vouchers/edit/${vchId}`, {
      state: {
        readOnly: !canEditVouchers,
        returnTo: back,
      },
    });
  };

  // Excel-style cells
  const cell = 'border border-slate-300 px-2.5 py-1.5 text-[14px] leading-snug';
  const cellNum = `${cell} text-right tabular-nums whitespace-nowrap`;
  const headCell = 'border border-slate-400 bg-slate-200 px-2.5 py-1.5 text-[13px] font-bold text-slate-700 uppercase tracking-wide sticky top-0 z-10';
  const headBtn = 'flex items-center select-none cursor-pointer hover:text-blue-700';

  // Opening / Total / Closing pin to the bottom of the scroll area as a block,
  // so the balances stay on screen while the transactions scroll behind them.
  // Stacked sticky rows need an exact offset each, and an auto-height row gives
  // none to compute from — so every summary row is pinned to the same explicit
  // height and the offsets are multiples of it. Any drift here shows up as the
  // rows overlapping or a gap opening between them.
  const FOOT_H = 32; // px — matches text-[14px] leading-snug + py-1.5 + borders
  const footRow = (rowsFromBottom: number) => ({
    height: FOOT_H,
    bottom: FOOT_H * rowsFromBottom,
  });

  return (
    <div className="flex flex-col w-full ledger-report-root fixed left-0 right-0 top-14 bottom-16 sm:static sm:h-full sm:top-auto sm:bottom-auto" style={{ overscrollBehavior: 'contain' }}>
      {/* Print-only header — only renders to paper. Browsers ignore display
          for screen because the @media print rules in index.css show it. */}
      <div className="print-only mb-3" aria-hidden>
        <h1 className="text-xl font-bold mb-1">Ledger Statement</h1>
        <div className="text-sm">
          <strong>{ledgerName || '—'}</strong>
          <span className="ml-3">From {displayDate(dateFrom)} to {displayDate(dateTo)}</span>
        </div>
      </div>
      {/* ── Header bar — title, filters and actions all on ONE row ──
           relative/z-20 so the ledger picker's absolute dropdown lays over the
           table below instead of being painted under it. flex-wrap is the
           narrow-window fallback: the filters drop to a second line rather than
           crushing the title and the buttons. */}
      <div className="flex-none relative z-20 bg-white border-b border-slate-200 px-4 py-2.5 flex items-center gap-2 flex-wrap print:hidden">
        <div className="flex items-center gap-1 min-w-0 flex-shrink-0">
          <button onClick={() => navigate(-1)} className="p-1.5 rounded-lg text-slate-500 hover:bg-slate-100 -ml-1 flex-shrink-0" title="Back">
            <ChevronLeft size={20} />
          </button>
          <h1 className="text-[17px] font-bold text-slate-800 truncate">Ledger Report</h1>
          {ledgerName && (
            <span className="text-[13px] font-medium text-blue-700 bg-blue-50 px-2 py-0.5 rounded flex-shrink-0 ml-1 truncate max-w-[130px]">
              {ledgerName}
            </span>
          )}
        </div>

        {/* Filters live inline in the header rather than in a band beneath it. */}
        {showControls && (
          <>
            {/* Ledger picker */}
            <div className="relative flex-1 min-w-[160px]">
              <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <input
                value={picker}
                onChange={e => { setPicker(e.target.value); setPickerOpen(true); }}
                onFocus={() => picker.trim() && setPickerOpen(true)}
                onBlur={() => setTimeout(() => setPickerOpen(false), 200)}
                placeholder={ledgerName ? 'Change ledger…' : 'Pick a ledger / party…'}
                autoFocus
                className="w-full pl-9 pr-7 py-1.5 border border-slate-300 rounded-lg text-[13px] outline-none focus:ring-2 focus:ring-blue-300 bg-white"
              />
              {picker && (
                <button onMouseDown={() => setPicker('')} className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600">
                  <X size={12} />
                </button>
              )}
              {/* min-w keeps ledger names readable now that the picker shares
                  the row and can be squeezed narrow. */}
              {pickerOpen && (pickerResults.length > 0 || pickerLoading) && (
                // Ledger names here run 40-60 characters and wrap to two lines,
                // so a result is ~70px tall. The old max-h-60 (240px) fitted
                // barely three and sliced the next one through its own text,
                // which reads as a rendering fault rather than a scroll edge.
                // Taller box, and each result is given a definite line-height so
                // a row's height does not drift with browser zoom.
                <div className="absolute z-30 mt-1 w-full min-w-[280px] bg-white border border-slate-300 rounded-lg shadow-lg max-h-[22rem] overflow-y-auto overscroll-contain">
                  {pickerLoading && <div className="px-3 py-2 text-xs text-slate-400">Searching…</div>}
                  {pickerResults.map((l: any) => (
                    <button key={l.id} onMouseDown={() => selectLedger(l)}
                      className="block w-full text-left px-3 py-2 text-[13px] leading-snug hover:bg-blue-50 border-b last:border-0">
                      {/* These names differ only by their suffix — (DELHI),
                          (GUJARAT), (KOLKATA) — so the end of the line is the
                          part that tells them apart and must not be cut. Two
                          lines is enough for all of them; break-words stops a
                          long unbroken token from spilling out sideways. */}
                      <div className="font-medium text-slate-800 line-clamp-2 break-words">{l.company}</div>
                      {l.ledgergroup_name && <div className="text-[11px] leading-snug text-slate-500 truncate">{l.ledgergroup_name}</div>}
                    </button>
                  ))}
                </div>
              )}
            </div>
            {/* Search within results */}
            <input
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Search vch no / remark…"
              className="flex-1 min-w-[130px] px-3 py-1.5 border border-slate-300 rounded-lg text-[13px] outline-none focus:ring-2 focus:ring-blue-300 bg-white"
            />
            {/* Date range */}
            <div className="flex items-stretch border border-slate-300 rounded-lg bg-white overflow-hidden flex-shrink-0">
              <div className="flex items-center gap-1 px-2 border-r border-slate-300 bg-slate-100">
                <Calendar size={12} className="text-slate-400" />
                <span className="text-[10px] text-slate-500 uppercase font-bold tracking-wide">From</span>
              </div>
              <input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)}
                className="text-[13px] text-slate-700 outline-none bg-transparent px-2 py-1 w-[112px]" />
              <div className="flex items-center px-2 border-l border-r border-slate-300 bg-slate-100">
                <span className="text-[10px] text-slate-500 uppercase font-bold tracking-wide">To</span>
              </div>
              <input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)}
                className="text-[13px] text-slate-700 outline-none bg-transparent px-2 py-1 w-[112px]" />
            </div>
            <button onClick={resetDates} title="Reset dates to FY"
              className="p-1.5 border border-slate-300 rounded-lg hover:bg-slate-100 bg-white text-slate-500 flex-shrink-0">
              <RotateCcw size={14} />
            </button>
            {ledgerId && (
              <button onClick={clearLedger}
                className="px-2.5 py-1.5 border border-slate-300 rounded-lg hover:bg-slate-100 bg-white text-[13px] text-slate-600 flex-shrink-0 whitespace-nowrap">
                <X size={12} className="inline mr-0.5" /> Clear
              </button>
            )}
          </>
        )}

        {/* ml-auto pins the actions right whether or not the filters are open. */}
        <div className="flex items-center gap-1 flex-shrink-0 ml-auto">
          <button onClick={() => setShowControls(s => !s)}
            className={`p-2 rounded-lg transition-colors ${showControls ? 'bg-blue-600 text-white' : 'text-slate-500 hover:bg-slate-100'}`}
            title="Search & filters (Alt+F2)">
            <Search size={18} />
          </button>
          <button onClick={() => setShowBillWise(true)} disabled={!ledgerId}
            className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-[12px] font-medium text-blue-700 bg-blue-50 border border-blue-200 hover:bg-blue-100 disabled:opacity-40"
            title={!ledgerId ? 'Pick a ledger first' : 'Bill-wise outstanding (ledger voucher outstanding)'}>
            <FileText size={14} /> Bill-Wise
          </button>
          <button onClick={handlePrint} disabled={!ledgerId || rows.length === 0}
            className="p-2 rounded-lg text-slate-500 hover:bg-slate-100 disabled:opacity-40"
            title={!ledgerId ? 'Pick a ledger first' : 'Print'}>
            <Printer size={16} />
          </button>
          <button onClick={load} className="p-2 rounded-lg text-slate-500 hover:bg-slate-100" title="Refresh">
            <RefreshCw size={16} className={loading ? 'animate-spin' : ''} />
          </button>
        </div>
      </div>

      {!ledgerId ? (
        <div className="bg-white border border-slate-300 p-12 text-center text-slate-500 text-sm">
          Pick a ledger / party from the search above to view their statement.
        </div>
      ) : (
        <>

          {/* Mobile flat list — Tally-style, shown only on xs screens (hidden on sm+) */}
          <div className="sm:hidden flex-1 min-h-0 overflow-auto bg-white" style={{ overscrollBehavior: 'contain' }}>
            {loading ? (
              <div className="py-10 text-center text-gray-400 text-sm">Loading…</div>
            ) : sorted.length === 0 ? (
              <div className="py-10 text-center text-gray-400 text-sm">No transactions in this range</div>
            ) : (() => {
              // Group voucher rows so multi-line vouchers appear as a single row.
              const groups: Row[][] = [];
              let cur: Row[] = [];
              for (const r of visibleRows) {
                if (r.is_first && cur.length) { groups.push(cur); cur = []; }
                cur.push(r);
              }
              if (cur.length) groups.push(cur);

              return (
                <>
                  {groups.map((grp, gi) => {
                    const first = grp[0];
                    const totalDr = grp.reduce((s, r) => s + r.debit, 0);
                    const totalCr = grp.reduce((s, r) => s + r.credit, 0);
                    const amountVal = totalDr > 0 ? totalDr : totalCr;
                    const amountSide = totalDr > 0 ? 'Dr' : 'Cr';
                    const subInfo = [
                      displayDate(first.vch_date),
                      first.vch_subtype_name && first.vch_subtype_name !== first.vch_type_name
                        ? first.vch_subtype_name
                        : null,
                      first.vch_no ? `#${first.vch_no}` : null,
                    ].filter(Boolean).join('  |  ');
                    return (
                      <div
                        key={`m-${first.vch_id}-${gi}`}
                        className="border-b border-gray-200"
                        onClick={() => openVoucher(first.vch_id)}
                      >
                        <div className="flex items-start justify-between px-4 py-3">
                          <div className="flex-1 min-w-0 pr-3">
                            <div className="font-bold text-gray-900 text-[15px] uppercase leading-snug">
                              {first.vch_type_name || '—'}
                            </div>
                            <div className="text-[13px] text-gray-500 mt-0.5">{subInfo}</div>
                            {first.particulars && (
                              <div className="text-[13px] text-gray-500 mt-0.5 truncate">{first.particulars}</div>
                            )}
                          </div>
                          <div className="shrink-0 text-right">
                            <span className="text-[15px] font-medium text-gray-900 tabular-nums">
                              {fmt(amountVal)} {amountSide}
                            </span>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </>
              );
            })()}
          </div>

          {/* Table — `print:overflow-visible print:max-h-none` lets every
              row paginate naturally onto paper instead of being clipped to
              the on-screen scroll viewport. */}
          {/* flex-1 min-h-0 (not a fixed max-h) so the table fills whatever
              height is left in the column — a hardcoded 100vh-220px guessed at
              the chrome above it and left dead space below when the guess ran
              short. min-h-0 is what actually lets a flex child scroll instead
              of growing past its parent. Matches the mobile list above.
              print:* releases the constraint so printing isn't clipped to one
              screenful. */}
          <div ref={scrollRef} className="hidden sm:block sm:flex-1 min-h-0 bg-white border border-slate-300 overflow-auto print:overflow-visible print:flex-none print:h-auto print:border-slate-400">
            <table className="border-collapse text-[14px] w-full">
              <thead>
                <tr>
                  <th className={`${headCell} text-left w-28`}>
                    <div className={headBtn} onClick={() => toggleSort('vch_date')}>Vch Date<SortIcon col="vch_date" /></div>
                  </th>
                  <th className={`${headCell} text-left`}>
                    <div className={headBtn} onClick={() => toggleSort('particulars')}>Particulars<SortIcon col="particulars" /></div>
                  </th>
                  <th className={`${headCell} text-left w-36`}>
                    <div className={headBtn} onClick={() => toggleSort('vch_no')}>Vch No.<SortIcon col="vch_no" /></div>
                  </th>
                  <th className={`${headCell} text-left w-28`}>
                    <div className={headBtn} onClick={() => toggleSort('vch_type_name')}>Vch Type<SortIcon col="vch_type_name" /></div>
                  </th>
                  <th className={`${headCell} text-right w-32`}>
                    <div className={headBtn + ' justify-end'} onClick={() => toggleSort('debit')}>Debit<SortIcon col="debit" /></div>
                  </th>
                  <th className={`${headCell} text-right w-32`}>
                    <div className={headBtn + ' justify-end'} onClick={() => toggleSort('credit')}>Credit<SortIcon col="credit" /></div>
                  </th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan={6} className={`${cell} text-center text-slate-400 py-6`}>Loading…</td></tr>
                ) : sorted.length === 0 ? (
                  <tr><td colSpan={6} className={`${cell} text-center text-slate-400 py-6`}>No transactions in this range</td></tr>
                ) : (() => {
                  // Walk rows; alternate zebra per voucher (not per contra
                  // line) so multi-line vouchers stay visually grouped.
                  let voucherIdx = -1;
                  return visibleRows.map(r => {
                    if (r.is_first) voucherIdx++;
                    const zebra = voucherIdx % 2 === 1 ? 'bg-slate-50' : 'bg-white';
                    const continuation = !r.is_first;
                    return (
                      <tr key={`${r.vch_id}-${r.particulars}-${r.is_first}-${r.is_last}`} className={`${zebra} hover:bg-blue-50`}>
                        <td className={`${cell} ${continuation ? 'border-t-transparent' : ''} text-slate-600 whitespace-nowrap tabular-nums`}>
                          {r.is_first ? displayDate(r.vch_date) : ''}
                        </td>
                        <td className={`${cell} ${continuation ? 'pl-6 text-slate-700 border-t-transparent' : 'text-slate-800'}`}>
                          <div>{r.particulars}</div>
                          {r.is_first && r.bill_names && (
                            <div className="text-xs text-slate-500 mt-0.5">
                              <span className="font-medium">Bill Refs:</span> {r.bill_names}
                            </div>
                          )}
                          {r.is_first && r.remark && (
                            <div className="text-xs text-slate-400 italic mt-0.5">
                              {r.remark}
                            </div>
                          )}
                        </td>
                        <td className={`${cell} ${continuation ? 'border-t-transparent' : ''} whitespace-nowrap`}>
                          {r.is_first ? (
                            <button
                              onClick={() => openVoucher(r.vch_id)}
                              className={r.vch_no
                                ? "text-blue-600 hover:text-blue-800 hover:underline font-medium"
                                : "text-slate-400 hover:text-blue-600 hover:underline cursor-pointer"}
                              title={canEditVouchers ? 'Open in edit mode' : 'View only — no edit permission'}
                            >
                              {r.vch_no || '—'}
                            </button>
                          ) : ''}
                        </td>
                        <td className={`${cell} ${continuation ? 'border-t-transparent' : ''} text-slate-700 whitespace-nowrap`}>
                          {r.is_first
                            ? ((r.vch_subtype_name && r.vch_subtype_name !== r.vch_type_name) ? r.vch_subtype_name : (r.vch_type_name || '—'))
                            : ''}
                        </td>
                        <td className={`${cellNum} ${continuation ? 'border-t-transparent' : ''} ${r.debit  > 0 ? 'text-emerald-700' : 'text-slate-300'}`}>
                          {r.debit  > 0 ? fmt(r.debit)  : '—'}
                        </td>
                        <td className={`${cellNum} ${continuation ? 'border-t-transparent' : ''} ${r.credit > 0 ? 'text-red-700' : 'text-slate-300'}`}>
                          {r.credit > 0 ? fmt(r.credit) : '—'}
                        </td>
                      </tr>
                    );
                  });
                })()}
                {/* Footer — Opening → Total → Closing, in that order, all
                    after the transactions. Total counts only the
                    transactions (it does NOT fold the opening into the
                    column sums). Closing = Opening + transactions net. */}
                {(() => {
                  const totalDr = +totals.debit.toFixed(2);
                  const totalCr = +totals.credit.toFixed(2);
                  return (
                    <>
                      <tr className="bg-amber-50 border-t-2 border-slate-300 sticky z-20" style={footRow(2)}>
                        <td className={cell + ' bg-amber-50 text-slate-500 italic'}>—</td>
                        <td className={cell + ' bg-amber-50 italic font-semibold text-slate-700'} colSpan={3}>Opening Balance</td>
                        <td className={cellNum + ' bg-amber-50 ' + (opening > 0 ? 'text-emerald-700 font-semibold' : 'text-slate-300')}>
                          {opening > 0 ? fmt(opening) : '—'}
                        </td>
                        <td className={cellNum + ' bg-amber-50 ' + (opening < 0 ? 'text-red-700 font-semibold' : 'text-slate-300')}>
                          {opening < 0 ? fmt(Math.abs(opening)) : '—'}
                        </td>
                      </tr>
                      <tr className="bg-slate-100 font-semibold sticky z-20" style={footRow(1)}>
                        <td className={cell + ' bg-slate-100 text-slate-500 italic'}>—</td>
                        <td className={cell + ' bg-slate-100 text-slate-700'} colSpan={3}>Total</td>
                        <td className={cellNum + ' bg-slate-100 text-emerald-800'}>{fmt(totalDr)}</td>
                        <td className={cellNum + ' bg-slate-100 text-red-800'}>{fmt(totalCr)}</td>
                      </tr>
                      <tr className="bg-slate-200 font-bold sticky z-20" style={footRow(0)}>
                        <td className={cell + ' bg-slate-200 text-slate-500 italic'}>—</td>
                        <td className={cell + ' bg-slate-200 italic text-slate-700'} colSpan={3}>Closing Balance</td>
                        <td className={cellNum + ' bg-slate-200 ' + (closing > 0 ? 'text-emerald-800' : 'text-slate-300')}>
                          {closing > 0 ? fmt(closing) : '—'}
                        </td>
                        <td className={cellNum + ' bg-slate-200 ' + (closing < 0 ? 'text-red-800' : 'text-slate-300')}>
                          {closing < 0 ? fmt(Math.abs(closing)) : '—'}
                        </td>
                      </tr>
                    </>
                  );
                })()}
              </tbody>
            </table>
          </div>

          {/* Mobile — Grand Total fixed above pagination */}
          {!loading && sorted.length > 0 && (() => {
            const totalDr = +totals.debit.toFixed(2);
            const totalCr = +totals.credit.toFixed(2);
            const openingAbs = Math.abs(opening);
            const openingSide = opening >= 0 ? 'Dr' : 'Cr';
            const closingAbs = Math.abs(closing);
            const closingSide = closing >= 0 ? 'Dr' : 'Cr';
            return (
              <div className="sm:hidden flex-none border-t border-gray-200 print:hidden">
                <div className="bg-blue-700 text-white px-4 py-2.5 flex justify-between items-center">
                  <span className="font-bold text-sm tracking-widest">GRAND TOTAL</span>
                  <span className="text-xs tabular-nums">Op: {fmt(openingAbs)} {openingSide}</span>
                </div>
                <div className="flex divide-x divide-gray-200 bg-white">
                  <div className="flex-1 px-3 py-2.5">
                    <div className="text-[10px] uppercase text-gray-400 font-bold tracking-wide">Debit</div>
                    <div className="text-[14px] font-bold text-emerald-700 tabular-nums mt-0.5">{fmt(totalDr)}</div>
                  </div>
                  <div className="flex-1 px-3 py-2.5">
                    <div className="text-[10px] uppercase text-gray-400 font-bold tracking-wide">Credit</div>
                    <div className="text-[14px] font-bold text-red-600 tabular-nums mt-0.5">{fmt(totalCr)}</div>
                  </div>
                  <div className="flex-1 px-3 py-2.5">
                    <div className="text-[10px] uppercase text-gray-400 font-bold tracking-wide">Closing {closingSide}</div>
                    <div className="text-[14px] font-bold text-blue-700 tabular-nums mt-0.5">{fmt(closingAbs)}</div>
                  </div>
                </div>
              </div>
            );
          })()}

          {/* Count only — the whole period is on screen, so there is nothing to
              page through. Still worth stating how much is loaded. */}
          {voucherGroups.length > 0 && (
            <div className="flex-none flex items-center justify-between px-3 py-2 border-t border-slate-200 bg-white text-xs text-slate-600 print:hidden">
              <div>
                {voucherGroups.length} voucher{voucherGroups.length === 1 ? '' : 's'}
                <span className="text-slate-400"> · {displayDate(dateFrom)} to {displayDate(dateTo)}</span>
              </div>
            </div>
          )}
        </>
      )}

      {showBillWise && ledgerId && (
        <BillWiseModal ledgerId={ledgerId} ledgerName={ledgerName} asOf={dateTo} onOpenVoucher={openVoucherFromReport} onClose={() => setShowBillWise(false)} />
      )}
    </div>
  );
}

// ── Tally-style "Ledger Voucher Outstanding" (bill-wise) ──
// Amounts are signed (+Dr / -Cr) and rounded to paise, so anything under a
// paise is a fully settled bill.
const isZeroAmt = (n: any) => Math.abs(Number(n || 0)) < 0.01;
// Selection key for the On Account row — can't collide with a bills[] index.
const ON_ACCOUNT_KEY = -1;

function BillWiseModal({ ledgerId, ledgerName, asOf, onOpenVoucher, onClose }: { ledgerId: number; ledgerName: string; asOf: string; onOpenVoucher: (vchId: number) => void; onClose: () => void }) {
  const { showError } = useToast();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState<any>(null);
  const [showDetails, setShowDetails] = useState(true);
  // Settled bills (pending ≈ 0) are noise in an outstanding report, so they are
  // hidden by default; the toggle brings them back for reconciliation.
  const [showZero, setShowZero] = useState(false);
  // Selected bill indices into data.bills; ON_ACCOUNT_KEY marks the On Account row.
  const [selected, setSelected] = useState<Set<number>>(new Set());
  const openVch = (id: number) => onOpenVoucher(id);
  useEffect(() => {
    let alive = true;
    setLoading(true);
    setSelected(new Set());
    vouchersApi.getLedgerBillWise({ ledger_id: ledgerId, as_of: asOf })
      .then(res => { if (alive && res.success) setData(res.data); })
      .catch((e: any) => showError('Error', e?.message || 'Failed to load bill-wise report'))
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [ledgerId, asOf, showError]);

  const money = (n: number) => Math.abs(Number(n || 0)).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  const drcr = (n: number) => (Number(n) >= 0 ? 'Dr' : 'Cr');
  const fmtD = (s: string | null) => { if (!s) return '—'; const d = new Date(s); return isNaN(d.getTime()) ? String(s).slice(0, 10) : d.toLocaleDateString('en-GB'); };

  // Bills carrying their original index so selection survives the zero filter.
  const visibleBills = useMemo(() => {
    const list: Array<{ b: any; i: number }> = (data?.bills || []).map((b: any, i: number) => ({ b, i }));
    return showZero ? list : list.filter(({ b }) => !isZeroAmt(b.pending_amount));
  }, [data, showZero]);
  const oaVisible = !!data?.on_account && (showZero || !isZeroAmt(data.on_account.amount));
  const hiddenCount = (data?.bills?.length || 0) - visibleBills.length + (data?.on_account && !oaVisible ? 1 : 0);

  // Totals are recomputed over what is actually on screen, so the columns still
  // add up when settled bills are hidden (their opening amounts drop out too).
  const shown = useMemo(() => {
    const opening = visibleBills.reduce((s, { b }) => s + Number(b.opening_amount || 0), 0);
    const pending = visibleBills.reduce((s, { b }) => s + Number(b.pending_amount || 0), 0);
    const oa = oaVisible ? Number(data?.on_account?.amount || 0) : 0;
    return {
      opening: +opening.toFixed(2),
      pending: +pending.toFixed(2),
      grandOpening: +(opening + oa).toFixed(2),
      grandPending: +(pending + oa).toFixed(2),
    };
  }, [visibleBills, oaVisible, data]);

  const selectableKeys = useMemo(
    () => [...visibleBills.map(v => v.i), ...(oaVisible ? [ON_ACCOUNT_KEY] : [])],
    [visibleBills, oaVisible],
  );
  const allSelected = selectableKeys.length > 0 && selectableKeys.every(k => selected.has(k));
  const someSelected = selectableKeys.some(k => selected.has(k));

  // Only rows currently on screen count — a hidden row can't contribute to a
  // total the user can't see.
  const sel = useMemo(() => {
    let count = 0, opening = 0, pending = 0;
    for (const { b, i } of visibleBills) {
      if (!selected.has(i)) continue;
      count++; opening += Number(b.opening_amount || 0); pending += Number(b.pending_amount || 0);
    }
    if (oaVisible && selected.has(ON_ACCOUNT_KEY)) {
      const oa = Number(data?.on_account?.amount || 0);
      count++; opening += oa; pending += oa;
    }
    return { count, opening: +opening.toFixed(2), pending: +pending.toFixed(2) };
  }, [visibleBills, selected, oaVisible, data]);

  const toggleOne = (key: number) => setSelected(prev => {
    const next = new Set(prev);
    if (next.has(key)) next.delete(key); else next.add(key);
    return next;
  });
  const toggleAll = () => setSelected(prev => (
    selectableKeys.every(k => prev.has(k)) ? new Set() : new Set(selectableKeys)
  ));

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-3" onClick={onClose}>
      <div className="bg-white rounded-lg shadow-xl w-full max-w-5xl max-h-[92vh] flex flex-col" onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div className="flex items-start justify-between px-4 py-3 border-b border-slate-200">
          <div>
            <div className="text-[15px] font-bold text-slate-800">Ledger Voucher Outstanding — Bill-Wise</div>
            <div className="text-[12px] text-slate-500">
              <span className="font-semibold text-slate-700">{data?.ledger_name || ledgerName}</span> · All Bills
              {asOf && <span> · As of {fmtD(asOf)}</span>}
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setShowZero(v => !v)}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded text-[12px] font-medium border transition-colors ${showZero ? 'bg-slate-700 border-slate-700 text-white hover:bg-slate-800' : 'bg-white border-slate-300 text-slate-600 hover:bg-slate-50'}`}
              title={showZero ? 'Hide fully settled bills (zero pending)' : 'Show fully settled bills (zero pending)'}>
              {showZero ? <EyeOff size={14} /> : <Eye size={14} />}
              {showZero ? 'Hide Zero' : 'Show Zero'}
            </button>
            <button
              onClick={() => setShowDetails(v => !v)}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded text-[12px] font-medium border transition-colors ${showDetails ? 'bg-blue-600 border-blue-600 text-white hover:bg-blue-700' : 'bg-white border-slate-300 text-slate-600 hover:bg-slate-50'}`}
              title={showDetails ? 'Hide ref-wise transaction details' : 'Show ref-wise transaction details'}>
              {showDetails ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
              {showDetails ? 'Hide Details' : 'Detailed'}
            </button>
            <button onClick={onClose} className="p-1 hover:bg-slate-100 rounded"><X size={18} /></button>
          </div>
        </div>

        {/* Selection summary — pinned above the scroll area so the selected
            closing total stays visible while scrolling the bills. */}
        {!loading && sel.count > 0 && (
          <div className="flex-none flex items-center justify-between gap-3 px-4 py-2 bg-blue-50 border-b border-blue-200 text-[12px]">
            <span className="font-semibold text-blue-900">
              {sel.count} selected
              <span className="ml-3 font-normal text-blue-700">
                Opening <span className="font-semibold tabular-nums">{money(sel.opening)} {drcr(sel.opening)}</span>
              </span>
            </span>
            <span className="flex items-center gap-3">
              <span className="text-blue-900">
                Selected Closing&nbsp;
                <span className="text-[14px] font-bold tabular-nums">{money(sel.pending)} <span className="font-normal text-blue-600">{drcr(sel.pending)}</span></span>
              </span>
              <button onClick={() => setSelected(new Set())}
                className="px-2 py-1 rounded border border-blue-300 bg-white text-blue-700 hover:bg-blue-100 font-medium">Clear</button>
            </span>
          </div>
        )}

        {/* Body — scrolls; the total is a separate pinned footer below. */}
        <div className="flex-1 min-h-0 overflow-auto">
          {loading ? (
            <div className="p-10 text-center text-slate-400 text-sm">Loading…</div>
          ) : !data || (data.bills.length === 0 && !data.on_account) ? (
            <div className="p-10 text-center text-slate-400 text-sm">No bill references for this ledger.</div>
          ) : visibleBills.length === 0 && !oaVisible ? (
            <div className="p-10 text-center text-slate-400 text-sm">
              Every bill is fully settled — nothing outstanding.
              <button onClick={() => setShowZero(true)} className="ml-1 text-blue-600 hover:underline">Show zero-value bills</button>
            </div>
          ) : (
            <table className="w-full text-[13px] table-fixed">
              <colgroup>
                <col className="w-10" /><col className="w-24" /><col /><col className="w-40" /><col className="w-40" /><col className="w-24" /><col className="w-24" />
              </colgroup>
              <thead className="bg-slate-50 text-slate-500 text-[11px] uppercase tracking-wide sticky top-0 z-10">
                <tr>
                  <th className="px-2 py-2 text-center">
                    <input
                      type="checkbox"
                      className="align-middle cursor-pointer accent-blue-600"
                      checked={allSelected}
                      ref={el => { if (el) el.indeterminate = someSelected && !allSelected; }}
                      onChange={toggleAll}
                      title="Select all shown bills" />
                  </th>
                  <th className="px-3 py-2 text-left">Date</th>
                  <th className="px-3 py-2 text-left">Ref No. / Particulars</th>
                  <th className="px-3 py-2 text-right">Opening Amount</th>
                  <th className="px-3 py-2 text-right">Pending Amount</th>
                  <th className="px-3 py-2 text-left">Due On</th>
                  <th className="px-3 py-2 text-right">Overdue days</th>
                </tr>
              </thead>
              {visibleBills.map(({ b, i: bi }) => {
                const settled = isZeroAmt(b.pending_amount);
                const checked = selected.has(bi);
                return (
                  <tbody key={bi} className="border-b border-slate-200">
                    {/* Bill header row — click anywhere on it to select the bill */}
                    <tr className={`cursor-pointer ${checked ? 'bg-blue-50' : 'bg-white hover:bg-slate-50'}`} onClick={() => toggleOne(bi)}>
                      <td className="px-2 py-1.5 text-center align-top">
                        <input type="checkbox" className="align-middle cursor-pointer accent-blue-600"
                          checked={checked} onChange={() => toggleOne(bi)} onClick={e => e.stopPropagation()} />
                      </td>
                      <td className="px-3 py-1.5 font-semibold text-slate-800 whitespace-nowrap tabular-nums align-top">{fmtD(b.bill_date)}</td>
                      <td className="px-3 py-1.5 font-semibold text-slate-800 align-top">{b.bill_name}</td>
                      <td className="px-3 py-1.5 text-right tabular-nums align-top">{money(b.opening_amount)} <span className="text-slate-400">{drcr(b.opening_amount)}</span></td>
                      <td className={`px-3 py-1.5 text-right tabular-nums font-bold align-top ${settled ? 'text-slate-300' : (b.pending_amount >= 0 ? 'text-slate-800' : 'text-red-700')}`}>{settled ? '—' : <>{money(b.pending_amount)} <span className="text-slate-400 font-normal">{drcr(b.pending_amount)}</span></>}</td>
                      <td className="px-3 py-1.5 whitespace-nowrap tabular-nums text-slate-600 align-top">{fmtD(b.bill_date)}</td>
                      <td className={`px-3 py-1.5 text-right tabular-nums align-top ${b.overdue_days > 0 && !settled ? 'text-red-600 font-semibold' : 'text-slate-400'}`}>{b.overdue_days ?? '—'}</td>
                    </tr>
                    {/* Allocation lines — click to open the voucher */}
                    {showDetails && b.lines.map((l: any, li: number) => {
                      const clickable = !!l.vch_id;
                      return (
                        <tr key={li}
                          onClick={() => clickable && openVch(l.vch_id)}
                          className={`text-[12px] text-slate-600 ${clickable ? 'cursor-pointer hover:bg-blue-50' : ''}`}
                          title={clickable ? 'Open this voucher' : ''}>
                          <td className="px-2 py-0.5"></td>
                          <td className="px-3 py-0.5 pl-6 whitespace-nowrap tabular-nums italic">{fmtD(l.date)}</td>
                          <td className="px-3 py-0.5 pl-6 italic">
                            {l.type}
                            {l.vch_no && <span className={`ml-2 ${clickable ? 'text-blue-600' : 'text-slate-400'}`}>{l.vch_no}</span>}
                            {clickable && <ExternalLink size={11} className="inline-block ml-1 mb-0.5 text-blue-400" />}
                          </td>
                          <td className="px-3 py-0.5 text-right tabular-nums italic">{money(l.amount)} <span className="text-slate-400">{l.drcr}</span></td>
                          <td colSpan={3} className="px-3 py-0.5"></td>
                        </tr>
                      );
                    })}
                  </tbody>
                );
              })}
              {/* Ref Sub Total + On Account (Tally-style) */}
              <tbody className="border-t border-slate-300">
                <tr className="font-semibold text-slate-700 italic">
                  <td className="px-2 py-1.5"></td>
                  <td className="px-3 py-1.5" colSpan={2}>
                    Sub Total ({visibleBills.length} bill{visibleBills.length === 1 ? '' : 's'})
                    {hiddenCount > 0 && <span className="ml-2 not-italic font-normal text-slate-400">· {hiddenCount} settled hidden</span>}
                  </td>
                  <td className="px-3 py-1.5 text-right tabular-nums">{money(shown.opening)} <span className="text-slate-400 font-normal">{drcr(shown.opening)}</span></td>
                  <td className="px-3 py-1.5 text-right tabular-nums">{money(shown.pending)} <span className="text-slate-400 font-normal">{drcr(shown.pending)}</span></td>
                  <td className="px-3 py-1.5" colSpan={2}></td>
                </tr>
                {oaVisible && (
                  <tr className={`text-amber-900 font-semibold italic cursor-pointer ${selected.has(ON_ACCOUNT_KEY) ? 'bg-amber-200' : 'bg-amber-100 hover:bg-amber-200'}`}
                    onClick={() => toggleOne(ON_ACCOUNT_KEY)}>
                    <td className="px-2 py-1.5 text-center">
                      <input type="checkbox" className="align-middle cursor-pointer accent-amber-600"
                        checked={selected.has(ON_ACCOUNT_KEY)} onChange={() => toggleOne(ON_ACCOUNT_KEY)} onClick={e => e.stopPropagation()} />
                    </td>
                    <td className="px-3 py-1.5 whitespace-nowrap tabular-nums">{fmtD(data.on_account.date)}</td>
                    <td className="px-3 py-1.5">On Account</td>
                    <td className="px-3 py-1.5 text-right tabular-nums">{money(data.on_account.amount)} <span className="opacity-60 font-normal">{data.on_account.drcr}</span></td>
                    <td className="px-3 py-1.5 text-right tabular-nums">{money(data.on_account.amount)} <span className="opacity-60 font-normal">{data.on_account.drcr}</span></td>
                    <td className="px-3 py-1.5" colSpan={2}></td>
                  </tr>
                )}
              </tbody>
            </table>
          )}
        </div>

        {/* Pinned grand-total footer */}
        {!loading && data && (visibleBills.length > 0 || oaVisible) && (
          <div className="flex-none border-t-2 border-slate-300 bg-slate-100">
            <table className="w-full text-[13px] font-bold table-fixed">
              <colgroup>
                <col className="w-10" /><col className="w-24" /><col /><col className="w-40" /><col className="w-40" /><col className="w-24" /><col className="w-24" />
              </colgroup>
              <tbody>
                <tr>
                  <td className="px-2 py-2"></td>
                  <td className="px-3 py-2" colSpan={2}>
                    Grand Total{oaVisible ? ' (incl. On Account)' : ''}
                    {hiddenCount > 0 && <span className="ml-2 font-normal text-slate-400 text-[12px]">of shown bills only</span>}
                  </td>
                  <td className="px-3 py-2 text-right tabular-nums">{money(shown.grandOpening)} <span className="text-slate-400 font-normal">{drcr(shown.grandOpening)}</span></td>
                  <td className="px-3 py-2 text-right tabular-nums">{money(shown.grandPending)} <span className="text-slate-400 font-normal">{drcr(shown.grandPending)}</span></td>
                  <td className="px-3 py-2" colSpan={2}></td>
                </tr>
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
